import { Fragment, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Check, ChevronsUpDown, Loader2, XCircle } from "lucide-react";
import { useLocation } from "react-router-dom";
import { Controller, useController, useForm } from "react-hook-form";
import { cn } from "@/utils/classnames";
import { useRefundDrawer } from "@/hooks/invoice/use-refund-drawer";
import { useInvoice } from "@/hooks/invoice/use-invoice";
import { useInvoices } from "@/hooks/invoice/use-invoices";
import { useIssueRefund } from "@/hooks/transaction/use-issue-refund";
import { useAuth } from "@/providers/auth";
import { convertThousandSeparator } from "@/scripts/functions";
import { addresseeNamePriority } from "@/utils/invoice";
import { formatPhoneUS } from "@/utils/functions";
import { InvoiceStatusBadge } from "./invoice-status-badge";
import { Button } from "@/components/ui/button-tw";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PAID_INVOICE_STATUS,
  PAID_OFF_PLATFORM_STATUS,
} from "@/constants/invoice";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const RETURN_REASONS = [
  { label: "Shipping/Transit Issue", value: "shipping_transit_issue" },
  { label: "Product Issue (Damaged, Defective)", value: "product_issue" },
  {
    label: "Customer Order Error/No Longer Needed",
    value: "customer_order_error",
  },
  { label: "Other", value: "other" },
];

function Loading() {
  return (
    <div role="status" className="px-6 py-8 animate-pulse">
      <div className="h-2.5 bg-gray-200 rounded-full dark:bg-gray-700 w-48 mb-4"></div>
      <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px] mb-2.5"></div>
      <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 mb-2.5"></div>
      <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[330px] mb-2.5"></div>
      <span className="sr-only">Loading...</span>
    </div>
  );
}

function Error() {
  return (
    <div className="rounded-md bg-red-50 p-4 m-6">
      <div className="flex">
        <div className="flex-shrink-0">
          <XCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">
            There was an error loading the invoice details.
          </h3>
          <div className="mt-2 text-sm text-red-700">
            <ul className="list-disc space-y-1 pl-5">
              <li>Make sure your internet connection is stable</li>
              <li>Try closing and opening the drawer again</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RefundDrawer() {
  const { open, handleClose, isLoading, isError } = useRefundDrawer();

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <div className="fixed inset-0" />

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full sm:pl-16">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="relative pointer-events-auto w-screen max-w-2xl">
                  <div className="pb-[120px] flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                    <div className="px-4 py-6 sm:px-6 bg-skin-primary">
                      <div className="flex items-start justify-between">
                        <Dialog.Title className="text-base font-semibold leading-6 text-white">
                          Issue Refund
                        </Dialog.Title>
                        <div className="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            className="relative rounded-md text-gray-50 hover:text-gray-300 focus:ring-2 focus:ring-white"
                            onClick={handleClose}
                          >
                            <span className="absolute -inset-2.5" />
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {isLoading ? (
                      <Loading />
                    ) : isError ? (
                      <Error />
                    ) : (
                      <div>
                        <div className="flex mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
                          <div className="my-6 min-w-0 w-full">
                            <h1 className="text-xl font-bold text-gray-900">
                              Issue a refund in just a few steps
                            </h1>
                          </div>
                        </div>
                        <div className="flex mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
                          <RefundStepper />
                        </div>
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

function RefundStepper() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { user, cognitoId, clientId, userRole } = useAuth();
  const userId =
    userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;
  const { mutate: issueRefund, isLoading: submitting } = useIssueRefund();
  const { handleClose } = useRefundDrawer();

  const { data: defaultInvoice } = useInvoice({
    invoiceId: queryParams.get("refund"),
    enabled: !!queryParams.get("refund"),
  });

  const { handleSubmit, control, setValue, watch } = useForm({
    values: {
      invoice: defaultInvoice || null,
      refundType: "full",
      returnReason: "",
    },
  });

  const selectedInvoiceId = watch("invoice")?.invoice_id;
  const { data: invoice } = useInvoice({
    invoiceId: selectedInvoiceId,
    enabled: !!selectedInvoiceId,
  });

  const onSubmit = ({ invoice, refundType, returnReason }) => {
    const name = addresseeNamePriority(invoice);
    const total = invoice?.invoice_info?.invoice_total;
    const pmType = invoice?.invoice_info?.invoice_payment_method_type;
    const last4 = invoice?.invoice_info?.invoice_payment_method_last_four;
    const pmLabel = pmType
      ? pmType.toLowerCase() === "ach"
        ? "ACH"
        : pmType
            .replace(/^[-_]*(.)/, (_, c) => c.toUpperCase())
            .replace(/[-_]+(.)/g, (_, c) => " " + c.toUpperCase())
      : null;

    const pmText = pmLabel
      ? ` to their ${pmLabel}${last4 ? ` ending in *${last4}` : ""}`
      : "";

    const auth_content = `By clicking confirm, you are issuing a refund to ${name} for $${convertThousandSeparator(
      total,
    )}${pmText}. They will receive an email confirmation for the refund.`;

    const payload = {
      platform_client_id: userId,
      invoice_id: invoice?.invoice_id,
      refund_amount: total,
      refund_type: refundType,
      reason: returnReason || undefined,
      auth_approved: true,
      auth_content,
    };

    issueRefund(payload, {
      onSuccess: () => {
        toast.success("Refund issued successfully!");
        handleClose();
      },
      onError: (err) => {
        const message =
          err?.response?.data?.error_description ||
          "Something went wrong issuing the refund. Please try again.";
        toast.error(message);
      },
    });
  };

  const businessName = addresseeNamePriority(invoice);
  const amount = invoice?.invoice_info?.invoice_total;
  const paymentMethodType = invoice?.invoice_info?.invoice_payment_method_type;
  const lastFour = invoice?.invoice_info?.invoice_payment_method_last_four;

  const paymentMethodLabel = paymentMethodType
    ? paymentMethodType?.toLowerCase() === "ach"
      ? "ACH"
      : paymentMethodType
          .replace(/^[-_]*(.)/, (_, c) => c.toUpperCase())
          .replace(/[-_]+(.)/g, (_, c) => " " + c.toUpperCase())
    : null;

  return (
    <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
      <ol role="list" className="overflow-hidden">
        {/* Step 1 — Invoice */}
        <li className="pb-10 relative">
          <div
            aria-hidden="true"
            className="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-gray-300"
          />
          <div
            aria-current="step"
            className="group relative flex items-start w-full"
          >
            <span aria-hidden="true" className="flex h-9 items-center">
              <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-skin-primary bg-white">
                <span className="h-2.5 w-2.5 rounded-full bg-skin-primary/95" />
              </span>
            </span>

            <div className="ml-4 flex flex-col grow mb-6">
              <div className="flex flex-col w-full mb-4">
                <div className="text-sm font-semibold leading-6 text-gray-900">
                  Select the invoice you are issuing a refund for
                </div>
                <div className="mt-2">
                  <PaidInvoiceCombobox
                    control={control}
                    name="invoice"
                    defaultInvoice={defaultInvoice}
                    setField={setValue}
                  />
                </div>
              </div>

              {invoice && (
                <div className="rounded-lg bg-gray-50 shadow-sm ring-1 ring-gray-900/5 pb-6">
                  <dl className="flex flex-wrap">
                    <div className="flex-auto pl-6 pt-6">
                      <dt className="text-sm font-medium leading-6 text-gray-500">
                        Amount paid
                      </dt>
                      <dd className="mt-1 text-lg font-semibold leading-6 text-gray-900">
                        $
                        {convertThousandSeparator(
                          invoice?.invoice_info?.invoice_total,
                        )}
                      </dd>
                    </div>
                    <div className="flex-none self-end px-6 pt-4">
                      <dt className="sr-only">Status</dt>
                      <dd>
                        <InvoiceStatusBadge
                          status={invoice?.invoice_info?.invoice_status}
                        />
                      </dd>
                    </div>
                    <div className="mt-6 flex w-full flex-none gap-x-4 border-t border-gray-900/5 px-6 pt-6">
                      <dd className="text-sm font-medium leading-6 text-gray-900">
                        <div>
                          {invoice?.invoice_info?.invoice_to?.business_name}
                        </div>
                        <div className="text-gray-400">
                          {invoice?.invoice_info?.invoice_to?.email}
                        </div>
                        <div className="text-gray-400">
                          {formatPhoneUS(
                            invoice?.invoice_info?.invoice_to?.phone,
                          )}
                        </div>
                      </dd>
                    </div>
                    <div className="flex justify-between w-full">
                      <div className="mt-4 flex flex-none gap-x-4 px-6 w-1/2">
                        <dt className="flex-none text-sm font-medium leading-6 text-gray-900">
                          Due on
                        </dt>
                        <dd className="text-sm leading-6 text-gray-500">
                          <time>
                            {dayjs(
                              invoice?.invoice_info?.invoice_due_date,
                            ).format("LL")}
                          </time>
                        </dd>
                      </div>
                      <div className="mt-4 flex flex-none gap-x-4 px-6">
                        <dt className="flex-none text-sm font-medium leading-6 text-gray-900">
                          Issued on
                        </dt>
                        <dd className="text-sm leading-6 text-gray-500">
                          <time>
                            {dayjs(
                              invoice?.invoice_info?.invoice_send_date,
                            ).format("LL")}
                          </time>
                        </dd>
                      </div>
                    </div>
                  </dl>
                </div>
              )}
            </div>
          </div>
        </li>

        {/* Step 2 — Refund Type */}
        <li className="pb-10 relative">
          <div
            aria-hidden="true"
            className="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-gray-300"
          />
          <div aria-current="step" className="group relative flex items-start">
            <span aria-hidden="true" className="flex h-9 items-center">
              <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-skin-primary bg-white">
                <span className="h-2.5 w-2.5 rounded-full bg-skin-primary/95" />
              </span>
            </span>
            <span className="ml-4 flex min-w-0 flex-col w-full mb-6">
              <span className="text-sm font-medium text-gray-900">
                Refund Type
              </span>
              <span className="text-sm text-gray-500 mb-2">
                Currently only full refunds are supported.
              </span>
              <Controller
                control={control}
                name="refundType"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select refund type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full">Full</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </span>
          </div>
        </li>

        {/* Step 3 — Return Reason (Optional) */}
        <li className="pb-10 relative">
          <div
            aria-hidden="true"
            className="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-gray-300"
          />
          <div aria-current="step" className="group relative flex items-start">
            <span aria-hidden="true" className="flex h-9 items-center">
              <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-skin-primary bg-white">
                <span className="h-2.5 w-2.5 rounded-full bg-skin-primary/95" />
              </span>
            </span>
            <span className="ml-4 flex min-w-0 flex-col w-full mb-6">
              <span className="text-sm font-medium text-gray-900">
                Return Reason{" "}
                <span className="text-gray-400 font-normal">(Optional)</span>
              </span>

              <Controller
                control={control}
                name="returnReason"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a reason..." />
                    </SelectTrigger>
                    <SelectContent>
                      {RETURN_REASONS.map((reason) => (
                        <SelectItem key={reason.value} value={reason.value}>
                          {reason.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </span>
          </div>
        </li>

        {/* Step 4 — Confirm and Issue Refund */}
        <li className="relative pb-20">
          <div aria-current="step" className="group relative flex items-start">
            <span aria-hidden="true" className="flex h-9 items-center">
              <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-skin-primary bg-white">
                <span className="h-2.5 w-2.5 rounded-full bg-skin-primary/95" />
              </span>
            </span>
            <span className="ml-4 flex min-w-0 flex-col gap-y-2 mb-6 w-full">
              <span className="text-sm font-medium text-gray-900">
                Confirm and Issue Refund
              </span>

              {invoice && (
                <div className="rounded-lg bg-gray-50 p-4 ring-1 ring-gray-900/5 text-sm text-gray-600 leading-relaxed">
                  By clicking confirm, you are issuing a refund to{" "}
                  <span className="font-medium text-gray-900">
                    {businessName}
                  </span>{" "}
                  for{" "}
                  <span className="font-medium text-gray-900">
                    ${convertThousandSeparator(amount)}
                  </span>
                  {paymentMethodLabel && (
                    <>
                      {" "}
                      to their{" "}
                      <span className="font-medium text-gray-900">
                        {paymentMethodLabel}
                      </span>
                      {lastFour && (
                        <>
                          {" "}
                          ending in{" "}
                          <span className="font-medium text-gray-900">
                            *{lastFour}
                          </span>
                        </>
                      )}
                    </>
                  )}
                  . They will receive an email confirmation for the refund.
                </div>
              )}

              <div className="mt-2">
                <Button type="submit" disabled={!invoice || submitting}>
                  Confirm
                  {submitting && (
                    <Loader2 className="h-4 w-4 ml-2 animate-spin" />
                  )}
                </Button>
              </div>
            </span>
          </div>
        </li>
      </ol>
    </form>
  );
}

function makePaidInvoiceLabel({ invoice_info }) {
  const {
    invoice_number: invNumber,
    invoice_total: total,
    invoice_due_date: due,
  } = invoice_info;
  return `#${invNumber} - ${addresseeNamePriority({
    invoice_info,
  })} - $${convertThousandSeparator(total)} - ${dayjs(due).format(
    "MM/DD/YYYY",
  )}`;
}

function filterPaidInvoices(invoices) {
  return invoices.filter(({ invoice_info }) =>
    [PAID_INVOICE_STATUS, PAID_OFF_PLATFORM_STATUS].includes(
      invoice_info.invoice_status,
    ),
  );
}

function PaidInvoiceCombobox({ control, name, setField, defaultInvoice }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(
    defaultInvoice
      ? {
          label: makePaidInvoiceLabel(defaultInvoice),
          value: `${defaultInvoice?.invoice_info?.invoice_number || ""} ${
            defaultInvoice?.invoice_info?.invoice_to?.business_dba || ""
          } ${defaultInvoice?.invoice_info?.invoice_to?.business_name || ""} ${
            defaultInvoice?.invoice_info?.invoice_to?.first_name || ""
          } ${defaultInvoice?.invoice_info?.invoice_to?.last_name || ""} ${
            defaultInvoice?.invoice_info?.invoice_to?.email || ""
          } ${defaultInvoice?.invoice_info?.invoice_total} ${
            defaultInvoice?.invoice_info?.invoice_due_date || ""
          }`,
          invoice: defaultInvoice,
        }
      : null,
  );

  const { data } = useInvoices({ select: filterPaidInvoices });
  const { field } = useController({ control, name });

  const invoices = useMemo(() => {
    return data?.map((invoice) => ({
      label: makePaidInvoiceLabel(invoice),
      value: `${invoice.invoice_info.invoice_number || ""} ${
        invoice.invoice_info.invoice_to.business_dba || ""
      } ${invoice.invoice_info.invoice_to.business_name || ""} ${
        invoice.invoice_info.invoice_to.first_name || ""
      } ${invoice.invoice_info.invoice_to.last_name || ""} ${
        invoice.invoice_info.invoice_to.email || ""
      } ${invoice.invoice_info.invoice_total} ${
        invoice.invoice_info.invoice_due_date || ""
      }`,
      invoice,
    }));
  }, [data]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {value ? value.label : "Select invoice..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[516px] p-0">
        <Command>
          <CommandInput placeholder="Search invoice..." />
          <CommandList>
            <CommandEmpty>No paid invoice found.</CommandEmpty>
            <CommandGroup>
              {invoices?.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.value}
                  onSelect={(currentValue) => {
                    const next = currentValue === value?.value ? null : item;
                    setValue(next);
                    setField("invoice", next ? next.invoice : null);
                    field.onChange(next ? next.invoice : null);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value?.value === item.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
